import {
  Subscription,
  SubscriptionViewModel,
  SubscriptionStatus,
} from "./types";
import i18n from "@core/i18n";

export class SubscriptionViewModelMapper {
  static toViewModel(subscription: Subscription): SubscriptionViewModel {
    const statusConfig = this.getStatusConfig(subscription.status);
    const isActive = subscription.status === "active";
    const isCanceled = subscription.status === "canceled";
    const isPastDue = subscription.status === "past_due";
    const isTrialing = subscription.status === "trialing";
    const isLifetime = subscription.plan.period === "lifetime";

    return {
      // Affichage
      planName: subscription.plan.name,
      amountLabel: this.formatAmount(
        subscription.plan.amount,
        subscription.plan.currency,
      ),
      periodLabel: this.getPeriodLabel(subscription.plan.period),
      statusLabel: statusConfig.label,
      statusClassName: statusConfig.className,

      // Dates
      nextBillingDate: isLifetime
        ? undefined
        : this.formatDate(subscription.currentPeriodEnd),
      renewalDate: isLifetime
        ? undefined
        : this.formatDate(subscription.currentPeriodEnd),
      trialEndDate: subscription.trialEnd
        ? this.formatDate(subscription.trialEnd)
        : undefined,

      // État
      status: subscription.status,
      isActive,
      isCanceled,
      isPastDue,
      isTrialing,
      isLifetime,

      // Actions disponibles
      canChangePlan: isActive && !subscription.cancelAtPeriodEnd,
      canCancelRenewal: isActive && !subscription.cancelAtPeriodEnd,
      canResumeRenewal: isCanceled,
      canUpdatePayment: isActive || isPastDue,
    };
  }

  private static getStatusConfig(status: SubscriptionStatus) {
    const configs = {
      active: {
        label: i18n.t("subscription.status.active"),
        className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
      },
      canceled: {
        label: i18n.t("subscription.status.canceled"),
        className: "bg-amber-500/15 text-amber-300 border-amber-500/30",
      },
      past_due: {
        label: i18n.t("subscription.status.pastDue"),
        className: "bg-red-500/15 text-red-300 border-red-500/30",
      },
      trialing: {
        label: i18n.t("subscription.status.trialing"),
        className: "bg-blue-500/15 text-blue-300 border-blue-500/30",
      },
      incomplete: {
        label: i18n.t("subscription.status.incomplete"),
        className: "bg-gray-500/15 text-gray-300 border-gray-500/30",
      },
    };

    return configs[status] || configs.active;
  }

  private static formatAmount(amount: number, currency: string): string {
    try {
      return new Intl.NumberFormat(i18n.language || "fr", {
        style: "currency",
        currency: currency || "EUR",
      }).format(amount / 100);
    } catch {
      // Devise inconnue → fallback nombre + code
      return `${(amount / 100).toFixed(2)} ${currency}`;
    }
  }

  private static getPeriodLabel(
    period: "monthly" | "yearly" | "lifetime",
  ): string {
    return period === "monthly"
      ? i18n.t("subscription.period.monthly")
      : period === "yearly"
        ? i18n.t("subscription.period.yearly")
        : i18n.t("subscription.period.lifetime");
  }

  private static formatDate(date: Date): string {
    return new Intl.DateTimeFormat(i18n.language || "fr", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  }

  // Validation helpers
  static validateSubscription(subscription: Subscription): string[] {
    const errors: string[] = [];

    if (!subscription.id) errors.push("Subscription ID is required");
    if (!subscription.plan) errors.push("Plan is required");
    if (!subscription.currentPeriodStart)
      errors.push("Period start is required");
    if (!subscription.currentPeriodEnd) errors.push("Period end is required");

    if (subscription.currentPeriodEnd <= subscription.currentPeriodStart) {
      errors.push("Period end must be after period start");
    }

    return errors;
  }

  static canPerformAction(
    subscription: Subscription,
    action: "changePlan" | "cancelRenewal" | "resumeRenewal" | "updatePayment",
  ): boolean {
    const vm = this.toViewModel(subscription);

    switch (action) {
      case "changePlan":
        return vm.canChangePlan;
      case "cancelRenewal":
        return vm.canCancelRenewal;
      case "resumeRenewal":
        return vm.canResumeRenewal;
      case "updatePayment":
        return vm.canUpdatePayment;
      default:
        return false;
    }
  }
}
